import React, { useRef, useContext } from "react";
import { toast } from "react-toastify";
import {
  Modal,
  Loader,
  Grid,
  Card,
  IconButton,
  Icons,
  Text,
  Popover,
  DateRangeText,
  Divider,
  Button,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import ColorSquare from "../../../components/common/ColorSquare";
import {
  defaultToastOptions,
  extractError,
  joinStrings,
} from "../../../utils/misc";
import { useToggle } from "../../../hooks/state";
import { useFeatureFlag } from "../../../context/features";
import { RECURRING_APPT_FEATURE_FLAG } from "../../../context/features/features.utils";
import AppointmentStatusChip from "../AppointmentStatusChip/AppointmentStatusChip";
import AppointmentActionsMenu from "../AppointmentActionsMenu/AppointmentActionsMenu";
import {
  useGetAppointmentScheduleItemQuery,
  useGetUserCreditsQuery,
} from "./AppointmentScheduleModal-generated.hooks";
import { useAppointments } from "../AppointmentsProvider/AppointmentsProvider";
import UserAvatar from "../../../components/common/UserAvatar/UserAvatar";

interface AppointmentScheduleModalProps {
  appointmentUuid?: string;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

const AppointmentScheduleModal = (props: AppointmentScheduleModalProps) => {
  const { visible, onClose } = props;
  return (
    <Modal
      visible={visible}
      closeModal={onClose}
      width="480px"
      disableClickAway
    >
      <AppointmentScheduleModalContent {...props} />
    </Modal>
  );
};

const AppointmentScheduleModalContent = (
  props: AppointmentScheduleModalProps,
) => {
  const { appointmentUuid, onClose, onSuccess } = props;

  // custom hooks

  const app = useContext(AppContext);
  const { isFeatureEnabled } = useFeatureFlag();
  const recurringBookingFlag = isFeatureEnabled(RECURRING_APPT_FEATURE_FLAG);
  const { showCreditModal, checkInAppointment, applyCredit, forgiveSchedule } =
    useAppointments();

  // states and ref

  const menuIconRef = useRef<HTMLButtonElement>(null);
  const [menuVisible, menuActions] = useToggle(false);
  const [checkInLoading, checkInActions] = useToggle(false);
  const [applyCreditLoading, applyCreditActions] = useToggle(false);
  const [forgiveLoading, forgiveActions] = useToggle(false);

  // api

  const { data, refetch } = useGetAppointmentScheduleItemQuery({
    variables: {
      uuid: appointmentUuid!,
    },
    skip: !appointmentUuid,
    fetchPolicy: "no-cache",
    onError(error) {
      toast.error(extractError(error), defaultToastOptions);
      onClose();
    },
  });

  const { data: credits, refetch: refetchCredits } = useGetUserCreditsQuery({
    variables: {
      input: {
        userUuid: data?.getAppointmentScheduleItem.user.uuid,
        appointmentTypeUuid:
          data?.getAppointmentScheduleItem.appointmentType.uuid,
      },
    },
    skip: !data,
    fetchPolicy: "no-cache",
    onError(error) {
      toast.error(extractError(error), defaultToastOptions);
      onClose();
    },
  });

  // callbacks

  const refetchAll = () => {
    refetch();
    refetchCredits();
  };

  const handleSuccess = () => {
    refetchAll();
    onSuccess();
  };

  const handleAddCredits = () => {
    if (!schedule.user || !schedule.appointmentType) {
      toast.error("User or appointment type inexistent!", defaultToastOptions);
      return;
    }
    showCreditModal(schedule.user.uuid, schedule.appointmentType.uuid, {
      onSuccess: refetchCredits,
    });
  };

  const handleCheckIn = async () => {
    checkInActions.on();
    checkInAppointment(appointmentUuid, {
      onSuccess,
      onError: refetchAll,
    }).finally(() => {
      checkInActions.off();
    });
  };

  const handleApplyCredit = async () => {
    applyCreditActions.on();
    applyCredit(appointmentUuid, {
      onSuccess,
      onError: refetchAll,
    }).finally(() => {
      applyCreditActions.off();
    });
  };

  const handleForgive = async () => {
    forgiveActions.on();
    forgiveSchedule(appointmentUuid, {
      onSuccess,
      onError: refetchAll,
    }).finally(() => {
      forgiveActions.off();
    });
  };

  const appointment = data?.getAppointmentScheduleItem;

  if (!appointment || !credits) {
    return (
      <Card className="p-4">
        <Grid container justifyContent="center" alignItems="center">
          <Loader />
        </Grid>
      </Card>
    );
  }

  const totalCredits = credits.getUserAppointmentCreditsCountByType.count;
  const hasCredits = totalCredits > 0;
  const schedule = data.getAppointmentScheduleItem;
  const isPaid = !!schedule.appointmentCredit;
  const isCanceled = schedule.eventStatus === "CANCELLED";
  const isLateCancel = schedule.isLateCancel;
  const isScheduled = schedule.eventStatus === "SCHEDULED";
  const isNoShowed = schedule.eventStatus === "NOSHOW";
  const hasUnpaidReason = !!schedule.unpaidReason;
  const isRecurring = !!schedule.template;

  // buttons visibility

  const addCreditsVisible =
    !hasCredits && !isPaid && (!isCanceled || isLateCancel);
  const payAndCheckInVisible =
    hasCredits && !isPaid && (isScheduled || isNoShowed);
  const payVisible =
    hasCredits &&
    !isPaid &&
    (isScheduled || isNoShowed || (isCanceled && isLateCancel));
  const checkInVisible = isPaid && (isScheduled || isNoShowed);
  const forgiveReturnCreditVisible =
    recurringBookingFlag &&
    isPaid &&
    (isNoShowed || (isCanceled && isLateCancel));
  const forgiveDontChargeVisible =
    recurringBookingFlag &&
    !isPaid &&
    !hasUnpaidReason &&
    (isNoShowed || (isCanceled && isLateCancel));

  const hasActions =
    addCreditsVisible ||
    payAndCheckInVisible ||
    payVisible ||
    checkInVisible ||
    forgiveReturnCreditVisible ||
    forgiveDontChargeVisible;

  const buttonMenuVisible = !isCanceled || !recurringBookingFlag;

  return (
    <>
      <Card>
        <div className="pt-4 pr-4 pb-4">
          <Grid container justifyContent="flex-end" spacing={3}>
            <Grid item>
              {buttonMenuVisible && (
                <IconButton
                  icon={Icons.Kebabs}
                  ref={menuIconRef}
                  onClick={menuActions.on}
                  tooltip="More actions"
                />
              )}
            </Grid>
            <Grid item>
              <IconButton icon={Icons.X} onClick={onClose} />
            </Grid>
          </Grid>
          <Grid container spacing={0} columnSpacing={3} className="mt-2">
            <Grid item xs={2} container justifyContent="center">
              <ColorSquare
                color={appointment.appointmentType.color}
                className="mt-2"
              />
            </Grid>
            <Grid item xs={10}>
              <Text variant="heading-4" color="wolf-800">
                {appointment.appointmentType.name}
              </Text>
              <DateRangeText
                variant="body-lg"
                color="wolf-600"
                className="mt-2"
                start={new Date(appointment.startTime)}
                end={new Date(appointment.endTime)}
                timeZone={app.client.timezone}
                showTime
              />
              {isRecurring && (
                <Text variant="body-lg" color="wolf-600" className="mt-2">
                  Recurring series
                </Text>
              )}
            </Grid>
            <Grid
              item
              xs={2}
              container
              justifyContent="center"
              alignItems="center"
            >
              <UserAvatar
                size="small"
                borderColor="wolf-200"
                data={appointment.staff}
              />
            </Grid>
            <Grid item xs={10}>
              <Text variant="body-lg" color="wolf-800">
                {joinStrings(
                  appointment.staff?.firstName,
                  appointment.staff?.lastName,
                )}
              </Text>
              <Text variant="body-md" color="wolf-600">
                Staff
              </Text>
            </Grid>
            <Grid
              item
              xs={2}
              container
              justifyContent="center"
              alignItems="center"
            >
              <UserAvatar
                size="small"
                borderColor="wolf-200"
                data={appointment.user}
              />
            </Grid>
            <Grid item xs={5}>
              <Text variant="body-lg" color="wolf-800">
                {joinStrings(
                  appointment.user.firstName,
                  appointment.user.lastName,
                )}
              </Text>
              <Text variant="body-md" color="wolf-600">
                <span>Member</span>
                <span className="mr-2 ml-2">·</span>
                <b>{totalCredits}</b>
                <span> credits</span>
              </Text>
            </Grid>
            <Grid
              item
              xs={5}
              container
              justifyContent="flex-end"
              alignItems="center"
            >
              <AppointmentStatusChip data={appointment} />
            </Grid>
          </Grid>
        </div>
        {hasActions && (
          <>
            <Divider />
            <div className="pt-0 pr-4 pb-4 pl-4">
              {addCreditsVisible && (
                <Button
                  text="Add credits"
                  fullWidth
                  size="large"
                  buttonType="secondary"
                  className="mt-4"
                  onClick={handleAddCredits}
                />
              )}
              {payAndCheckInVisible && (
                <Button
                  text="Pay and check in"
                  fullWidth
                  size="large"
                  buttonType="primary"
                  className="mt-4"
                  loading={checkInLoading}
                  onClick={handleCheckIn}
                />
              )}
              {payVisible && (
                <Button
                  text="Pay (apply credit)"
                  fullWidth
                  size="large"
                  buttonType="secondary"
                  className="mt-4"
                  loading={applyCreditLoading}
                  onClick={handleApplyCredit}
                />
              )}
              {checkInVisible && (
                <Button
                  text="Check in"
                  fullWidth
                  size="large"
                  buttonType="primary"
                  className="mt-4"
                  loading={checkInLoading}
                  onClick={handleCheckIn}
                />
              )}
              {forgiveReturnCreditVisible && (
                <Button
                  text="Forgive (return credit)"
                  fullWidth
                  size="large"
                  buttonType="secondary"
                  className="mt-4"
                  loading={forgiveLoading}
                  onClick={handleForgive}
                />
              )}
              {forgiveDontChargeVisible && (
                <Button
                  text="Forgive (don't charge)"
                  fullWidth
                  size="large"
                  buttonType="secondary"
                  className="mt-4"
                  loading={forgiveLoading}
                  onClick={handleForgive}
                />
              )}
            </div>
          </>
        )}
      </Card>
      <Popover
        anchorEl={menuIconRef.current!}
        visible={menuVisible}
        onClose={menuActions.off}
      >
        <AppointmentActionsMenu
          userUuid={appointment.user?.uuid}
          appointmentUuid={appointmentUuid!}
          expiresAt={appointment.appointmentCredit?.expiresAt}
          status={appointment.eventStatus}
          showFixedActions
          onClose={menuActions.off}
          onSuccess={handleSuccess}
        />
      </Popover>
    </>
  );
};

export default AppointmentScheduleModal;
